import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { onStoreEvent } from '@/presentation/stores/utils/storeEventBus.ts'
import {
  type SchoolPeriodUser,
  SchoolPeriodUserInput
} from '@/core/domain/school/user/SchoolPeriodUser.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import FetchAllSchoolPeriodUsersUseCase from '@/core/useCases/school/user/FetchAllSchoolPeriodUsersUseCase.ts'
import PrepareSchoolPeriodUserEditUseCase from '@/core/useCases/school/user/PrepareSchoolPeriodUserEditUseCase.ts'
import { CreateSchoolPeriodUserUseCase } from '@/core/useCases/school/user/CreateSchoolPeriodUserUseCase.ts'
import { UpdateSchoolPeriodUserUseCase } from '@/core/useCases/school/user/UpdateSchoolPeriodUserUseCase.ts'
import { AddExistingMembersToSchoolPeriodUseCase } from '@/core/useCases/school/user/AddExistingMembersToSchoolPeriodUseCase.ts'
import type { AvailableEducationalMember } from '@/core/domain/school/user/AvailableEducationalMember.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import SchoolPeriodUserMapper from '@/core/infrastructure/mappers/school/period/SchoolPeriodUserMapper.ts'
import { DeleteSchoolPeriodUserUseCase } from '@/core/useCases/school/user/DeleteSchoolPeriodUser.ts'

export const useSchoolPeriodUserStore = defineStore('SchoolPeriodUser', () => {
  // BASE COMMUNE
  const baseStore = createStoreBase([
    'fetchUsers',
    'prepareEdit',
    'createUser',
    'updateUser',
    'addExistingMembers',
    'deleteSchoolPeriodUser'
  ])

  // ÉTAT
  const schoolPeriodUsers = ref<SchoolPeriodUser[]>([])
  const currentEditUser = ref<SchoolPeriodUserInput | null>(null)
  const currentSchoolId = ref<number | null>(null)
  const currentSchoolPeriodId = ref<number | null>(null)

  // ACTIONS
  /**
   * Récupère tous les utilisateurs d'une période scolaire
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @param forceRefresh Force le rechargement des données même si déjà présentes
   */
  async function fetchAllSchoolPeriodUsersForSchool(
    schoolId: number,
    schoolPeriodId: number,
    forceRefresh = false
  ): Promise<SchoolPeriodUser[]> {
    // Éviter le rechargement inutile si les données sont déjà là pour la même période
    if (
      !forceRefresh &&
      schoolPeriodUsers.value.length > 0 &&
      currentSchoolId.value === schoolId &&
      currentSchoolPeriodId.value === schoolPeriodId &&
      baseStore.lastUpdated.value &&
      Date.now() - baseStore.lastUpdated.value.getTime() < 5 * 60 * 1000 // 5 minutes
    ) {
      return schoolPeriodUsers.value
    }

    try {
      baseStore.loading.value.fetchUsers = true
      baseStore.resetError()

      currentSchoolId.value = schoolId
      currentSchoolPeriodId.value = schoolPeriodId

      const response = await DependencyContainer.resolve(FetchAllSchoolPeriodUsersUseCase).execute(
        schoolId,
        schoolPeriodId
      )

      schoolPeriodUsers.value = response
      baseStore.updateLastUpdated()

      return response
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.fetchUsers = false
    }
  }

  /**
   * Prépare les données pour l'édition d'un utilisateur
   * @param schoolId Identifiant de l'établissement
   * @param schoolPeriodId Identifiant de la période scolaire
   * @param schoolPeriodUser Utilisateur à modifier
   */
  async function prepareSchoolPeriodUserEdit(
    schoolId: number,
    schoolPeriodId: number,
    schoolPeriodUser: SchoolPeriodUser
  ): Promise<SchoolPeriodUserInput> {
    try {
      baseStore.loading.value.prepareEdit = true
      baseStore.resetError()

      const response = await DependencyContainer.resolve(
        PrepareSchoolPeriodUserEditUseCase
      ).execute(schoolId, schoolPeriodId, schoolPeriodUser)

      currentEditUser.value = response
      return response
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.prepareEdit = false
    }
  }

  /**
   * Crée un nouvel utilisateur dans une période scolaire
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @param schoolPeriodUserInput Données de l'utilisateur
   */
  async function createSchoolPeriodUser(
    schoolId: number,
    schoolPeriodId: number,
    schoolPeriodUserInput: SchoolPeriodUserInput
  ): Promise<SchoolPeriodUser> {
    try {
      baseStore.loading.value.createUser = true
      baseStore.resetError()

      // Check validity
      const errors = SchoolPeriodUserMapper.validateSchoolPeriodUserInput(schoolPeriodUserInput)
      if (errors.size > 0) {
        setTimeout(() => Array.from(errors.values()).flat()
          .forEach((e) => {
            baseStore.showToast({ severity: 'error', summary: 'Erreur de validation', detail: e, life: 5000 })
          }), 0)
        return Promise.reject(new Error('Erreurs de validation'))
      }

      const response = await DependencyContainer.resolve(CreateSchoolPeriodUserUseCase).execute(
        schoolId,
        schoolPeriodId,
        schoolPeriodUserInput
      )

      // Ajouter l'utilisateur créé à la liste
      schoolPeriodUsers.value.push(response)

      // Réinitialiser l'utilisateur en cours d'édition
      currentEditUser.value = null

      baseStore.showSuccessToast(
        'Utilisateur créé',
        "L'utilisateur a bien été ajouté à la période scolaire."
      )

      baseStore.updateLastUpdated()
      return response
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.createUser = false
    }
  }

  /**
   * Met à jour un utilisateur existant dans une période scolaire
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @param schoolPeriodUserInput Données mises à jour de l'utilisateur
   */
  async function updateSchoolPeriodUser(
    schoolId: number,
    schoolPeriodId: number,
    schoolPeriodUserInput: SchoolPeriodUserInput
  ): Promise<SchoolPeriodUser> {
    try {
      baseStore.loading.value.updateUser = true
      baseStore.resetError()

      // Check validity
      const errors = SchoolPeriodUserMapper.validateSchoolPeriodUserInput(schoolPeriodUserInput)
      if (errors.size > 0) {
        setTimeout(() => Array.from(errors.values()).flat()
          .forEach((e) => {
            baseStore.showToast({ severity: 'error', summary: 'Erreur de validation', detail: e, life: 5000 })
          }), 0)
        return Promise.reject(new Error('Erreurs de validation'))
      }

      const response = await DependencyContainer.resolve(UpdateSchoolPeriodUserUseCase).execute(
        schoolId,
        schoolPeriodId,
        schoolPeriodUserInput
      )

      // Mettre à jour l'utilisateur dans la liste (et non l'ajouter)
      const index = schoolPeriodUsers.value.findIndex(
        (user) => user.userIdentifier === schoolPeriodUserInput.identifier
      )

      if (index !== -1) {
        schoolPeriodUsers.value[index] = response
      } else {
        // Si l'utilisateur n'existe pas encore dans la liste, l'ajouter
        schoolPeriodUsers.value.push(response)
      }

      // Réinitialiser l'utilisateur en cours d'édition
      currentEditUser.value = null

      baseStore.showSuccessToast(
        'Utilisateur mis à jour',
        "L'utilisateur a bien été mis à jour dans la période scolaire."
      )

      baseStore.updateLastUpdated()
      return response
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.updateUser = false
    }
  }

  /**
   * Associe des membres existants à une période scolaire
   * @param schoolId Identifiant de l'école
   * @param schoolPeriodId Identifiant de la période scolaire
   * @param members Membres à associer
   * @param defaultRole Rôle par défaut à attribuer
   */
  async function addExistingMembersToSchoolPeriod(
    schoolId: number,
    schoolPeriodId: number,
    members: AvailableEducationalMember[]
  ): Promise<{
    successful: SchoolPeriodUser[]
    failed: Array<{ member: AvailableEducationalMember; error: string }>
  }> {
    try {
      baseStore.loading.value.addExistingMembers = true
      baseStore.resetError()

      const response = await DependencyContainer.resolve(
        AddExistingMembersToSchoolPeriodUseCase
      ).execute(schoolId, schoolPeriodId, members)

      // Ajouter les membres ajoutés avec succès à la liste
      if (response.successful.length > 0) {
        schoolPeriodUsers.value.push(...response.successful)

        baseStore.showSuccessToast(
          'Membres ajoutés',
          `${response.successful.length} membre(s) ont été ajoutés à la période scolaire.`
        )
      }

      // Afficher les erreurs s'il y en a
      if (response.failed.length > 0) {
        const errorMessage = `${response.failed.length} membre(s) n'ont pas pu être ajoutés.`
        baseStore.showErrorToast("Erreurs lors de l'ajout", errorMessage)
      }

      baseStore.updateLastUpdated()
      return response
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err)
    } finally {
      baseStore.loading.value.addExistingMembers = false
    }
  }

  /**
   * Suppression d'un SchoolPeriodUser
   * @param schoolId
   * @param schoolPeriodId
   * @param userIdentifier
   */
  async function deleteSchoolPeriodUser(schoolId: number, schoolPeriodId: number, userIdentifier: string): Promise<void> {
    try  {
      baseStore.loading.value.deleteSchoolPeriodUser = true
      await DependencyContainer.resolve(DeleteSchoolPeriodUserUseCase).execute(schoolId, schoolPeriodId, userIdentifier)
      schoolPeriodUsers.value = schoolPeriodUsers.value.filter(user => user.userIdentifier !== userIdentifier)
    } catch (err) {
      baseStore.setError(err)
      return Promise.reject(err as Error)
    } finally {
      baseStore.loading.value.deleteSchoolPeriodUser = false
    }
  }

  /**
   * Réinitialise l'utilisateur en cours d'édition
   */
  function resetCurrentEditUser(): void {
    currentEditUser.value = null
  }

  /**
   * Réinitialise le store à son état initial
   */
  function reset(): void {
    schoolPeriodUsers.value = []
    currentEditUser.value = null
    currentSchoolId.value = null
    currentSchoolPeriodId.value = null
    baseStore.resetBase()
  }

  // GETTERS
  /**
   * Filtre les utilisateurs par rôle
   */
  const getUsersByRole = computed(() => {
    return (role: UserRole.Values): SchoolPeriodUser[] => {
      return schoolPeriodUsers.value.filter((user) => user.role.includes(role))
    }
  })

  /**
   * Récupère un utilisateur par son identifiant
   */
  const getUserByIdentifier = computed(() => {
    return (userIdentifier: string): SchoolPeriodUser | undefined => {
      return schoolPeriodUsers.value.find((user) => user.userIdentifier === userIdentifier)
    }
  })

  /**
   * Liste de l'équipe pédagogique (enseignants, CPE, personnel de direction)
   */
  const pedagogicalTeam = computed(() =>
    schoolPeriodUsers.value.filter(
      (user) =>
        user.role.includes(UserRole.Values.TEACHER) ||
        user.role.includes(UserRole.Values.CPE) ||
        user.role.includes(UserRole.Values.PERDIR)
    )
  )

  /**
   * Liste des enseignants
   */
  const teachers = computed(() =>
    schoolPeriodUsers.value.filter((user) => user.role.includes(UserRole.Values.TEACHER))
  )

  /**
   * Liste des étudiants
   */
  const students = computed(() =>
    schoolPeriodUsers.value.filter((user) => user.role.includes(UserRole.Values.STUDENT))
  )

  /**
   * Indique si les données doivent être rechargées
   */
  const needsFreshData = computed(() => {
    if (!baseStore.lastUpdated.value) return true

    // Considérer les données comme périmées après 5 minutes
    const fiveMinutesInMs = 5 * 60 * 1000
    return Date.now() - baseStore.lastUpdated.value.getTime() > fiveMinutesInMs
  })

  /**
   * Nombre total d'utilisateurs
   */
  const totalUsers = computed(() => schoolPeriodUsers.value.length)

  // Écouter l'événement de changement d'établissement pour se réinitialiser
  onStoreEvent('school:changed', () => {
    reset()
  })

  return {
    // État
    ...baseStore,
    schoolPeriodUsers,
    currentEditUser,
    currentSchoolId,
    currentSchoolPeriodId,
    schoolPeriodUserStoreLoading: baseStore.loading,
    schoolPeriodUserStoreError: baseStore.error,
    schoolPeriodUserStoreLastUpdated: baseStore.lastUpdated,

    // Actions
    fetchAllSchoolPeriodUsersForSchool,
    prepareSchoolPeriodUserEdit,
    createSchoolPeriodUser,
    updateSchoolPeriodUser,
    addExistingMembersToSchoolPeriod,
    deleteSchoolPeriodUser,
    resetCurrentEditUser,
    reset,

    // Getters
    getUsersByRole,
    getUserByIdentifier,
    pedagogicalTeam,
    teachers,
    students,
    needsFreshData,
    totalUsers,
    schoolPeriodUserStoreIsLoading: baseStore.isLoading
  }
})
